import React, { useMemo } from "react";
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Image, Platform, TextInput } from "react-native";
import { Trans, useTranslation } from 'react-i18next';
import GradientBackground from '../../../../uiComponents/GradientBackground';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import {
  openMoneygramPrivacyPolicy,
  openMoneygramPrivacyPolicyUsEs,
  openMoneygramTermsAndConditions,
  openMoneygramTermsAndConditionsUsEs,
} from "../../../../../helper/moneygramLegalLinks";
import { buildSendAdditionalInfoDisplayText } from "../../../../../helper/sendValidationHelper";
import EnglishFlag from "../../../../../../../../assets/images/english-flag.png";
import DownArrow from "../../../../../../../../assets/images/DownArrowIcon.svg";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import  FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";

import { Flag } from "../../../../Flag";
import { capturePosthogEvent } from "../../../../../utils/analytics";

interface ReviewFinalStepProps {
  onContinue: () => void;
  onEditStep: (key: string) => void;
  /** API error from update/validate flow; shown in the selection area */
  selectionError?: string;
  testID?: string | undefined;
}

const ReviewFinalStep: React.FC<ReviewFinalStepProps> = ({ onEditStep, onContinue, selectionError, testID }) => {
  const { t, i18n } = useTranslation();
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const currentSendAmount = useSessionStore((s: SessionState) => (s as any).currentSendAmount);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedCountryName = useSessionStore((s: SessionState) => s.selectedDestinationCountryName);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => s.selectedReceiveCurrency);
  const feeLookupParams = useSessionStore((s: SessionState) => (s as any).sendFeeLookupReviewEditStore);
  const receiverFirstName = useSessionStore((s: SessionState) => (s as any).receiverFirstName);
  const receiverMiddleName = useSessionStore((s: SessionState) => (s as any).receiverMiddleName);
  const receiverLastName = useSessionStore((s: SessionState) => (s as any).receiverLastName);
  const receiverLastName2 = useSessionStore((s: SessionState) => (s as any).receiverLastName2);
  const senderFirstName = useSessionStore((s: SessionState) => (s as any).senderFirstName);
  const senderMiddleName = useSessionStore((s: SessionState) => (s as any).senderMiddleName);
  const senderLastName = useSessionStore((s: SessionState) => (s as any).senderLastName);
  const senderLastName2 = useSessionStore((s: SessionState) => (s as any).senderLastName2);
  const senderAddress = useSessionStore((s: SessionState) => (s as any).senderAddress);
  const senderCity = useSessionStore((s: SessionState) => (s as any).senderCity);
  const senderZipCode = useSessionStore((s: SessionState) => (s as any).senderZipCode);
  const senderState = useSessionStore((s: SessionState) => (s as any).senderState);
  const senderDOB = useSessionStore((s: SessionState) => (s as any).senderDOB);
  const senderDOBString = useSessionStore((s: SessionState) => (s as any).senderDOBString);
  const phoneNumber = useSessionStore((s: SessionState) => (s as any).phoneNumber);
  const customerReceiveNumber = useSessionStore((s: SessionState) => (s as any).customerReceiveNumber);
  const senderPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).senderPhotoIdNumber);
  // fetch additional information dynamically
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;
  const missingRequiredFields = useSessionStore((s: SessionState) => (s as any).missingRequiredFields) as any[];
  const partnerName = useSessionStore((s: SessionState) => (s as any).partnerName);
  const partnerAddress = useSessionStore((s: SessionState) => (s as any).partnerAddress);

  const option: any = pendingSendOption || selectedSendOption || null;

  const selectedDeliveryOptionStore = useSessionStore((s: SessionState) => (s as any).selectedDeliveryOptionStore);
  const lastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).lastFeeLookupResponse);

  const getValuesFromLatestFeeLookupResponse = lastFeeLookupResponse?.FeeInfos.find((f: any) => {
    const sameDisplay = (f?.DeliveryOption || '') === (selectedDeliveryOptionStore?.DeliveryOption || '');
    return sameDisplay;
  });

  
  const summary = useMemo(() => {
    const sendCurrency: string = getValuesFromLatestFeeLookupResponse?.SendAmounts?.SendCurrency || "USD";
    const amountFromState = Number(String(currentSendAmount || "0").replace(/,/g, ""));
    const amountFromOption = Number(getValuesFromLatestFeeLookupResponse?.SendAmounts?.SendAmount ?? 0);
    const sendAmountNum: number = isFinite(amountFromOption) && amountFromOption > 0
      ? amountFromOption
      : (isFinite(amountFromState) ? amountFromState : 0);
    // Use same option-derived receive amount/currency as ReviewAndEditStep (ValidReceiveAmount/EstimatedReceiveAmount, not ReceiveAmounts)
    const receiveCurrency: string = selectedReceiveCurrency || (option?.ReceiveAmounts?.ValidCurrencyIndicator
      ? (option?.ValidReceiveCurrency || '')
      : (option?.EstimatedReceiveCurrency || option?.ValidReceiveCurrency || '')) || (getValuesFromLatestFeeLookupResponse ? FeeLookupHelper.getReceiveCurrency(getValuesFromLatestFeeLookupResponse) : "");
    const receiveAmount: number = Number(option?.ValidReceiveAmount ?? option?.EstimatedReceiveAmount ?? 0) || (getValuesFromLatestFeeLookupResponse ? FeeLookupHelper.getReceiveAmount(getValuesFromLatestFeeLookupResponse) : 0);
    const exchangeRateNum: number = getValuesFromLatestFeeLookupResponse ? FeeLookupHelper.getExchangeRate(getValuesFromLatestFeeLookupResponse) : 0;
    const fees: number = Number(getValuesFromLatestFeeLookupResponse?.SendAmounts?.TotalSendFees || 0);
    const total: number = Number(getValuesFromLatestFeeLookupResponse?.TotalAmount);

    return {
      sendAmount: sendAmountNum,
      sendCurrency,
      receiveAmount,
      receiveCurrency,
      exchangeRate: exchangeRateNum,
      fees,
      total,
    };
  }, [option, currentSendAmount, selectedReceiveCurrency, getValuesFromLatestFeeLookupResponse]);

  const promoCodeValue = useMemo(() => {
    try {
      const arr: any[] = Array.isArray((feeLookupParams as any)?.promoCodes) ? (feeLookupParams as any).promoCodes : [];
      return String(arr[0] ?? "");
    } catch { return ""; }
  }, [feeLookupParams]);

  // Promo code is read-only on this screen

  const receiverName = useMemo(() => {
    return [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]
      .filter(Boolean)
      .join(" ");
  }, [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]);

  const senderName = useMemo(() => {
    return [senderFirstName, senderMiddleName, senderLastName, senderLastName2]
      .filter(Boolean)
      .join(" ");
  }, [senderFirstName, senderMiddleName, senderLastName, senderLastName2]);

  const senderAddressFull = useMemo(() => {
    const parts = [
      senderAddress,
      // [senderCity, (senderState || selectedDestinationStateAbbr)].filter(Boolean).join(", "),
      // senderZipCode
      [senderCity, senderState, senderZipCode].filter(Boolean).join(", "),
      ,
    ].filter(Boolean);
    return parts.join("\n");
  }, [senderAddress, senderCity, senderState, selectedDestinationStateAbbr, senderZipCode]);

  const idNumberToShow = customerReceiveNumber || senderPhotoIdNumber || "";

  const dobToShow = useMemo(() => {
    if (senderDOBString) return senderDOBString;
    if (senderDOB instanceof Date && !isNaN((senderDOB as any))) {
      const mm = String(senderDOB.getMonth() + 1).padStart(2, "0");
      const dd = String(senderDOB.getDate()).padStart(2, "0");
      const yyyy = senderDOB.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
    return "";
  }, [senderDOBString, senderDOB]);

  const additionalInfo = useMemo(() => {
    return buildSendAdditionalInfoDisplayText(
      missingRequiredFields,
      additionalFieldValues,
    );
  }, [additionalFieldValues, missingRequiredFields]);

  const showAdditionalInformation = additionalInfo.length > 0;

  const termsAgreementLinkComponents = useMemo(
    () => ({
      termsLink: (
        <Text
          style={[styles.link, { textDecorationLine: "underline" }]}
          onPress={
            i18n.language.startsWith("es")
              ? openMoneygramTermsAndConditionsUsEs
              : openMoneygramTermsAndConditions
          }
          accessibilityRole="link"
        />
      ),
      privacyLink: (
        <Text
          style={[styles.link, { textDecorationLine: "underline" }]}
          onPress={
            i18n.language.startsWith("es")
              ? openMoneygramPrivacyPolicyUsEs
              : openMoneygramPrivacyPolicy
          }
          accessibilityRole="link"
        />
      ),
    }),
    [i18n.language]
  );

  const exchangeNoticeComponents = useMemo(
    () => ({
      bold: <Text style={styles.bold} />,
    }),
    []
  );


  console.log('DATTTTTT=>', summary.sendAmount, summary.sendAmount.toFixed(2))
  const data = {
    sendAmount: summary.sendAmount.toFixed(2),
    sendCurrency: summary.sendCurrency,
    receiveAmount: summary.receiveAmount.toFixed(2),
    receiveCurrency: summary.receiveCurrency || "—",
    receiverName: receiverName || "",
    destinationCountry: selectedCountryName +' '+ '(' +selectedDestinationCountryCode+')' || "",
    destinationState: selectedDestinationStateAbbr || "",
    receiveMethod: option?.DeliveryOption || "",
    senderName: senderName || "",
    senderAddress: senderAddressFull || "",
    phoneNumber: phoneNumber || "",
    idNumber: idNumberToShow || "",
    dob: dobToShow || "",
    exchangeRate: summary.exchangeRate ? summary.exchangeRate.toFixed(4) : "0.0000",
    available: "Instantly",
    fees: summary.fees.toFixed(2),
    total: summary.total.toFixed(2),
    additionalInformation: additionalInfo || "",
  };

  const formatPhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const maskedIDNumber = useMemo(() => {
      const raw = String(data.idNumber || '');
      if (!raw) return '';
      const last2 = raw.slice(-2);
      const first2 = raw.slice(0, 2)
      const maskLength = Math.max(0, raw.length - 4);
      return `${first2}${'*'.repeat(maskLength)}${last2}`;
    }, [data.idNumber]);

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>
      {/* <Text style={styles.subtitle}>{t("REVIEW_SUBTITLE")}</Text> */}
      <View>
        <Text style={styles.inputLabel}>{t("CVS_STORE_LOCATION")}</Text>
        <Text style={[styles.inputLabel, {fontSize: 16, lineHeight: 24, color: Colors.titleTextColor}]}>{partnerAddress}</Text>
      </View>
      <View style={[styles.sectionView, { marginTop: 24 }]}>
        <View>
          <Text style={[styles.inputLabel, {marginBottom: 2}]}>{t("YOU_SEND")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{data.sendAmount}</Text>
            <Text style={styles.currencyText}>{data.sendCurrency}</Text>
          </View>
          <Text style={[styles.inputLabel, {marginTop: 8, marginBottom: 2}]}>{t("THEY_RECEIVE")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{data.receiveAmount}</Text>
            <Text style={styles.currencyText}>{data.receiveCurrency}</Text>
          </View>
        </View>
        <TouchableOpacity testID="sendingAmountEntryButton"
          style={styles.editButton} 
          onPress={() => onEditStep?.('sendingAmountEntry')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          {/* <Text style={[styles.inputLabel]}>{t("REVIEW_DESTINATION_COUNTRY")}</Text> */}
          <View style={[styles.amountInput, { alignItems: 'center'}]}>
            <Flag iso3={selectedDestinationCountryCode ?? ''} width={24} height={24} />
            <View style={{ marginLeft: 16, flex: 1, minWidth: 0 }}>
              <Text style={[styles.inputLabel]}>{t("REVIEW_DESTINATION_COUNTRY")}</Text>
              <Text style={[styles.CountryText, styles.reviewValueMultiline]}>{data.destinationCountry}</Text>
            </View>
          </View>
        </View>
        <TouchableOpacity testID="countryButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('country')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      { (selectedDestinationCountryCode === 'USA' || selectedDestinationCountryCode === 'CAN') &&
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("DESTINATION_STATE")}</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
            <Text style={[styles.CountryText, styles.reviewValueMultiline]}>{data.destinationState}</Text>
          </View>
        </View>
        <TouchableOpacity testID="stateButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('state')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      }
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("RECEIVER_NAME")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{data.receiverName}</Text>
        </View>
        <TouchableOpacity testID="receiverNameButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('receiverName')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("RECEIVE_METHOD_TITLE")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{pendingSendOption?.DeliveryOptDisplayName}</Text>
        </View>
        <TouchableOpacity testID="howTheyReceiveButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('howTheyReceive')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.yourInfoStyle}>{t("YOUR_INFO")}</Text>
      <View style={[styles.sectionView, { marginTop: 16}]}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("SENDER_NAME")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{data.senderName}</Text>
        </View>
        <TouchableOpacity testID="senderNameButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('senderName')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("ADDRESS")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{data.senderAddress}</Text>
        </View>
        <TouchableOpacity testID="senderAddressButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('senderAddress')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("PHONE_NUMBER")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{formatPhoneNumber(data.phoneNumber)}</Text>
        </View>
        <TouchableOpacity testID="phoneButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('phone')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.sectionView}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("ID_NUMBER")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{maskedIDNumber}</Text>
        </View>
        <TouchableOpacity testID="senderIdNumberButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('senderIdNumber')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      <View style={[styles.sectionView, { marginBottom: !showAdditionalInformation ? 32 : 0}]}>
        <View style={styles.textContainer}>
          <Text style={[styles.inputLabel]}>{t("DOB")}</Text>
          <Text style={[styles.pickUpText, styles.reviewValueMultiline]}>{data.dob}</Text>
        </View>
        <TouchableOpacity testID="senderDOBButton"
          style={styles.editButton}
          onPress={() => onEditStep?.('senderDOB')}>
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>
      { showAdditionalInformation &&
        <View style={[styles.sectionView, { marginBottom: 16 }]}>
          <View style={styles.textContainer}>
            <Text style={[styles.inputLabel]}>{t("ADDITIONAL_INFORMATION")}</Text>
            <Text
              style={[styles.pickUpText, styles.reviewValueMultiline, styles.additionalInformationValue]}
            >
              {data.additionalInformation}
            </Text>
          </View>
          <TouchableOpacity testID="additionalInformationButton"
            style={styles.editButton}
            onPress={() => onEditStep?.('additionalInformation')}>
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      }
      {/* <View style={styles.promoContainer}>
        <TextInput
          style={styles.promoInput}
          placeholder="Promo code"
          value={promoCodeValue}
          editable={false}
        />
      </View> */}
      {/* <FloatingTextInputComponent
        testID="sendFinalReview"
        label={t("PROMO_CODE")}
        value={promoCodeValue}
        onChangeText={(val) => {
          // setPromoCode?.(val);
        }}
        editable={false}
        // handleApplyPromoCode={handleApplyPromoCode} //Temporarily removed Promocode for this screen
      /> */}
        {/* <RowItem
          label="DOB"
          value={data.dob}
          editable
          onEditStep={() => onEditStep("dob")}
        /> */}

        {/* Terms */}
      <View style={{ marginBottom: 16 }}>
        <View>
          <Text style={styles.terms}>
            <Trans
              i18nKey="BILL_PAY_FINAL_TERMS_AGREEMENT"
              components={termsAgreementLinkComponents}
            />
          </Text>
          <Text style={[styles.terms, { marginTop: 16 }]}>
            <Trans
              i18nKey="BILL_PAY_FINAL_EXCHANGE_NOTICE"
              components={exchangeNoticeComponents}
            />
          </Text>
        </View>
      </View>
              {/* <View><View style={styles.viewTap}/></View> */}
      {/* <GradientBackground style={styles.gradientCard}>
        <View style={styles.Crow}>
          <Text style={styles.feeLabel}>{t("EXHANGE_RATE")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>1 {data.sendCurrency} = {data.exchangeRate} {data.receiveCurrency}</Text>
          </View>
        </View>
        <View style={styles.Crow}>
          <Text style={styles.feeLabel}>{t("FEES")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{data.fees} {data.sendCurrency}</Text>
          </View>
        </View>
        <View style={styles.Crow}>
          <Text style={[styles.feeLabel, { textDecorationLine: "underline"}]}>{t("REVIEW_TOTAL")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{data?.total} {data?.sendCurrency}</Text>
          </View>
        </View>
        <TouchableOpacity testID="continueButton" 
          onPress={onContinue}
          activeOpacity={0.8}
          style={styles.continueButton}
        >
          <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
        </TouchableOpacity>
      </GradientBackground> */}
    {/* </ScrollView> */}
    </View>
  );
};

const RowItem = ({
  label,
  value,
  editable,
  onEditStep,
}: {
  label: string;
  value: string;
  editable?: boolean;
  onEditStep?: () => void;
}) => (
  <View style={styles.row}>
    <View>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={styles.rowValue}>{value}</Text>
    </View>
    {editable && (
      <TouchableOpacity onPress={onEditStep} testID="editButton">
        <Text style={styles.editBtn}>Edit</Text>
      </TouchableOpacity>
    )}
  </View>
);

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
    justifyContent: "flex-start",
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '400',
    fontFamily: 'Rubik',
    color: Colors.subTitleColor,
    lineHeight: 22,
    alignSelf: 'stretch'
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  pickUpText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: Colors.titleTextColor,
    lineHeight: 24
  },
  yourInfoStyle: {
    fontFamily: 'Rubik',
    fontSize: 20,
    fontWeight: '500',
    color: Colors.titleTextColor,
    lineHeight: 26,
    marginTop: 32
  },
  textContainer: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    marginRight: 8,
  },
  reviewValueMultiline: {
    flexShrink: 1,
    alignSelf: 'stretch',
  },
  additionalInformationValue: {
    flexShrink: 1,
    alignSelf: 'stretch',
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: '500',
    color: Colors.modalTitle,
    lineHeight: 36
  },
  sectionView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 32
  },
  currencyText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '500',
    color: Colors.subTitleColor,
    lineHeight: 24,
    paddingLeft: 8
  },
  editButton: {
    flexShrink: 0,
    backgroundColor: Colors.searchBackground,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14
  },
  editText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    color: Colors.modalTitle,
    lineHeight: 16
  },
  CountryText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 24
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
    alignItems: "center",
  },
  rowLabel: { fontSize: 14, color: "#555" },
  rowValue: { fontSize: 16, fontWeight: "600", marginTop: 2 },
  editBtn: { color: "#000", fontWeight: "500" },
  terms: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    color: Colors.subTitleColor,
  },
  link: {
    color: Colors.titleTextColor,
    fontWeight: "500",
  },
  bold: { fontWeight: "600" },
  bottomCard: {
    borderTopWidth: 1,
    borderColor: "#eee",
    padding: 16,
    backgroundColor: "#fafafa",
  },
  total: { marginTop: 8, fontSize: 16, fontWeight: "700" },
  continueBtn: {
    marginTop: 16,
    backgroundColor: "#000",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  continueText: { color: "#fff", fontSize: 16, fontWeight: "600" },
  gradientCard: {
    alignItems: "center",
    justifyContent: 'center',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    position: "absolute",
    right: 0,
    left: 0,
    top: "100%",
    transform: [{ translateX: -10 }],
    // width: 327,
  },
  viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius:24,
    marginBottom: 24,
    alignSelf: 'center'
  },
  promoContainer: {
    flexDirection: 'row',
    height: 56,
    padding: 24,
    borderRadius: 8,
    backgroundColor: Colors.searchCompanyBackground,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 24
  },
  promoInputWrapper: {
    flex: 1,
    position: 'relative',
    marginRight: 12,
  },
  promoInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#B3B3B3',
    paddingVertical: 8,
    paddingRight: 40,
    paddingLeft: 12,
  },
  clearButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    bottom: 8,
    width: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearIconCircle: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: '#B3B3B3',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  promoText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: Colors.titleTextColor,
    lineHeight: 18
  },
  promoApplyButton: {
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#B3B3B3'
  },
  applyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: Colors.applyText,
    lineHeight: 18,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  Crow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: 16,
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: "black",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    // marginHorizontal: 30,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  continueButton: {
    alignSelf: "center",
    backgroundColor: Colors.black,
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    shadowColor: Colors.black,
  },
  continueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.white,
    lineHeight: 24
  },
});

export default ReviewFinalStep;